import {
  defaultSitePaginationConfig,
  type CDLQueryParams,
  type Site,
  type RecordEntityId,
  type DeliveryDestination,
  type LocalMenuGroupToSiteRelationship,
  P2Role,
  type StationBrand,
  type SiteLocation,
  CountryCode,
  type Multigroup,
  type SitesUsingPriceLevelResponse,
  type MenuHoursStationDetail,
} from '@centric-os/types';
import type {
  PatchLocationBrandBulkBody,
  PostLocationGroupBatchGetResponse,
} from '@compassdigital/sdk.typescript/interface/location';
import { defineStore, storeToRefs, type StateTree } from 'pinia';
import { useAuthStore } from '@centric-os/stores/auth/use-auth-store';
import { isEmpty } from 'lodash-es';
import { useKDSStore } from '@centric-os/stores/sites/use-kds-store';
import { useMultigroupStore } from '@centric-os/stores/sites/use-multigroup-store';

interface State extends StateTree {
  site: Site;
  sites: Site[];
  siteDeliveryDestinations: DeliveryDestination[];
  updateSiteOnNextMount: boolean;
  siteLocalMenuGroups: LocalMenuGroupToSiteRelationship[];
  sitesUsingPriceLevel: SitesUsingPriceLevelResponse['sites'];
  // Per-station Menu Hours details, keyed by station id (lazily loaded from the edge).
  menuHoursDetails: Record<string, MenuHoursStationDetail>;
  // should be temporaly
  loadingAllSites: boolean;
  editSite: boolean;
}
const DEFAULT_SITES_LIST_PARAMS: CDLQueryParams = {
  sortBy: 'created_at',
  sortOrder: 'DESC',
};

type SitesListInFlight = { key: string; promise: Promise<void> };

type SitesListFlight = {
  abortController: AbortController | null;
  inFlight: SitesListInFlight | null;
};

type SitesListStore = {
  sites: Site[];
  pagination?: { queryParams?: CDLQueryParams };
  updatePaginationWithMeta?: (meta: unknown) => void;
};

async function resolveSitesListParams(
  store: SitesListStore,
  noPagination: boolean,
  params: CDLQueryParams | undefined,
  defaultParams: CDLQueryParams,
): Promise<CDLQueryParams> {
  const authStore = useAuthStore();
  const multigroupStore = useMultigroupStore();

  const isSiteOperator = authStore.hasRole(P2Role.SITE_OPERATOR);

  if (!isSiteOperator) {
    await multigroupStore.getMultiGroups();
  }

  let resolvedParams = params ?? defaultParams;

  if (!noPagination) {
    resolvedParams = store.pagination?.queryParams ?? defaultParams;
  }

  if (
    !isEmpty(multigroupStore.getMultigroupIDs) &&
    !isEmpty(resolvedParams) &&
    isEmpty(resolvedParams.id_multigroups)
  ) {
    resolvedParams = { ...resolvedParams, id_multigroups: multigroupStore.getMultigroupIDs };
  }

  return resolvedParams;
}

async function runSitesListRequest(
  store: SitesListStore,
  flight: SitesListFlight,
  noPagination: boolean,
  params: CDLQueryParams | undefined,
  fetchPage: (
    query: CDLQueryParams,
    signal: AbortSignal,
  ) => Promise<{ results: Site[]; meta: unknown }>,
): Promise<void> {
  const resolvedParams = await resolveSitesListParams(
    store,
    noPagination,
    params,
    DEFAULT_SITES_LIST_PARAMS,
  );
  const key = `${noPagination}:${JSON.stringify(resolvedParams)}`;

  // if identical request already in flight, share its promise, to avoid new fetch & abort
  if (flight.inFlight?.key === key) {
    return flight.inFlight.promise;
  }

  // new/different request supersedes any stale in-flight one (search-as-you-type).
  flight.abortController?.abort();
  flight.abortController = new AbortController();
  const { signal } = flight.abortController;

  const promise = (async () => {
    try {
      const { results, meta } = await fetchPage(resolvedParams, signal);
      // discard stale results i.e user typed another character in the sites seach bar
      if (signal.aborted) return;

      store.sites = results;

      if (!noPagination) {
        store.updatePaginationWithMeta?.(meta);
      }
    } catch (err) {
      // occurs when req aborted by a newer one, swallow it
      // we don't want to throw an error each time a previous req is aborted
      if (signal.aborted) return;

      throw err;
    }
  })().finally(() => {
    if (flight.inFlight?.key === key) flight.inFlight = null;
  });

  flight.inFlight = { key, promise };
  return promise;
}

export function useSiteStore(storeId = 'site') {
  const getSitesFlight: SitesListFlight = { abortController: null, inFlight: null };
  const getDiscountSitesFlight: SitesListFlight = { abortController: null, inFlight: null };
  const getAnnouncementSitesFlight: SitesListFlight = { abortController: null, inFlight: null };

  const store = defineStore(storeId, {
    state: (): State => ({
      site: null,
      sites: [],
      siteDeliveryDestinations: [],
      updateSiteOnNextMount: false,
      siteLocalMenuGroups: [],
      sitesUsingPriceLevel: [],
      menuHoursDetails: {},
      // should be temporaly
      loadingAllSites: false,
      editSite: false,
    }),
    actions: {
      async getSites(
        noPagination: boolean = false,
        params: CDLQueryParams = DEFAULT_SITES_LIST_PARAMS,
      ): Promise<void> {
        return runSitesListRequest(
          this,
          getSitesFlight,
          noPagination,
          params,
          async (query, signal): Promise<{ results: Site[]; meta: unknown }> => {
            const response = await this.serviceApi.get_centricos_sites({
              // edge service contract declares limit/page/sortOrder as required
              // but currently callers omit them, and edge service applies default values

              // @ts-expect-error FIXME: CDLQueryParams vs generated query type (TS2322)
              query,
              signal,
            });
            return {
              // @ts-expect-error SDK Site vs hand-maintained Site (TS2322)
              results: response.results,
              meta: response.meta,
            };
          },
        );
      },
      /**
       * Lightweight site list for discount pickers (`GET /centricos/discounts/sites`).
       * Returns `{ id, name }` only — resolve Thrive/Boost `appName` after selection.
       */
      async getDiscountSites(
        noPagination: boolean = false,
        params?: CDLQueryParams,
      ): Promise<void> {
        return runSitesListRequest(
          this,
          getDiscountSitesFlight,
          noPagination,
          params,
          async (query, signal): Promise<{ results: Site[]; meta: unknown }> => {
            const response = await this.serviceApi.get_centricos_discounts_sites({
              // @ts-expect-error CDLQueryParams vs generated query type (TS2322)
              query,
              signal,
            });
            // Lightweight payload is id+name only; Site[] keeps picker option binding typed.
            return {
              results: response.results,
              meta: response.meta,
            };
          },
        );
      },
      /**
       * Lightweight site list for announcement pickers (`GET /centricos/announcements/sites`).
       * Returns `{ id, name }` only — AdminSites falls back for `sector_name` on select.
       */
      async getAnnouncementSites(
        noPagination: boolean = false,
        params?: CDLQueryParams,
      ): Promise<void> {
        return runSitesListRequest(
          this,
          getAnnouncementSitesFlight,
          noPagination,
          params,
          async (query, signal): Promise<{ results: Site[]; meta: unknown }> => {
            const response = await this.serviceApi.get_centricos_announcements_sites({
              // @ts-expect-error CDLQueryParams vs generated query type (TS2322)
              query,
              signal,
            });
            // Lightweight payload is id+name only; Site[] keeps picker option binding typed.
            return {
              results: response.results,
              meta: response.meta,
            };
          },
        );
      },
      resetAndClearFilter(): void {
        // Resets and clears the site search query
        this.$reset();
        delete this.pagination.queryParams.filter;
      },
      async getSite(siteId: RecordEntityId, extended = true): Promise<void> {
        this.site = await this.fetchSite(siteId, extended);
      },
      /** Fetch a full site without mutating store.site. */
      async fetchSite(siteId: RecordEntityId, extended = true): Promise<Site> {
        const response = await this.serviceApi.get_location_group(String(siteId), {
          query: { nocache: true, extended, include_estimated_wait_time: false },
        });
        // @ts-expect-error SDK Group vs hand-maintained Site (TS2322)
        return response;
      },
      async getSiteDetailsPage(
        siteId: RecordEntityId,
        extended = true,
        includeDeleted = false,
      ): Promise<void> {
        await this.serviceApi
          .get_location_group(String(siteId), {
            query: {
              nocache: true,
              extended: extended,
              include_estimated_wait_time: false,
              include_deleted: includeDeleted,
            },
          })
          .then(async (response) => {
            // @ts-expect-error FIXME: SDK Group vs hand-maintained Site (TS2322)
            this.site = response;

            // Fetch KDS devices for the site BUs
            const kdsStore = useKDSStore();
            await kdsStore.fetchKDSDevices();

            // Fetch delivery destinations for the site
            await Promise.all([
              this.getSiteDeliveryDestinations(siteId, includeDeleted),
              this.getSitesLocalMenuGroups(siteId),
            ]);
          })
          .catch((err) => {
            if (err?.status === 401) {
              useAuthStore().logout();
            }
            throw err;
          });
      },
      /**
       * Lazily loads per-station Menu Hours details (the "Show in App" toggle gate and state)
       * from the centricos edge and stores them keyed by station id.
       */
      async getMenuHoursDetails(siteId: RecordEntityId): Promise<void> {
        const response = await this.serviceApi.get_centricos_site_menu_hours_details(
          String(siteId),
        );
        this.menuHoursDetails = Object.fromEntries(
          (response?.stations ?? []).map((station) => [station.id, station]),
        );
      },
      async getSiteNoExtended(siteId: RecordEntityId): Promise<Site> {
        const response = await this.serviceApi.get_location_group(String(siteId), {
          query: { nocache: true, include_estimated_wait_time: false },
        });
        // @ts-expect-error FIXME: SDK Group vs hand-maintained Site (TS2322)
        return response;
      },
      async getSitesByIds(siteIds: RecordEntityId[]): Promise<Site[]> {
        if (siteIds.length === 0) return [];
        const uniqueIds = Array.from(new Set(siteIds.map(String)));
        const batchSize = 50;
        const requests: Promise<PostLocationGroupBatchGetResponse>[] = [];
        for (let index = 0; index < uniqueIds.length; index += batchSize) {
          requests.push(
            this.serviceApi.post_location_group_batch_get({
              ids: uniqueIds.slice(index, index + batchSize),
            }),
          );
        }
        const responses = await Promise.allSettled(requests);
        // @ts-expect-error FIXME: SDK Group vs hand-maintained Site (TS2322)
        return responses.flatMap((response) =>
          response.status === 'fulfilled' ? response.value.groups ?? [] : [],
        );
      },
      async updateSite(siteId: RecordEntityId, payload: Partial<Site>): Promise<void> {
        // @ts-expect-error FIXME: hand-maintained Site vs SDK Group body (TS2345)
        const response = await this.serviceApi.patch_location_group(String(siteId), payload);
        // @ts-expect-error FIXME: SDK Group vs hand-maintained Site (TS2322)
        this.site = response;
      },
      async updateLocationSequence(
        id: RecordEntityId,
        payload: Partial<SiteLocation>,
      ): Promise<void> {
        // @ts-expect-error FIXME: hand-maintained SiteLocation vs SDK Location body (TS2345)
        const updatedLocation = await this.serviceApi.patch_location(String(id), payload);
        const index = this.site.locations.findIndex(
          (location) => location.id === updatedLocation.id,
        );
        if (index !== -1) {
          // @ts-expect-error FIXME: SDK Location vs hand-maintained SiteLocation (TS2322)
          this.site.locations[index] = updatedLocation;
        }
      },
      async getSiteDeliveryDestinations(
        siteId: RecordEntityId,
        includeDeleted = false,
      ): Promise<void> {
        const response = await this.serviceApi.get_location_group_deliverydestinations(
          String(siteId),
          { query: { include_deleted: includeDeleted } },
        );

        // Sort the delivery destinations by sort_sequence without mutating the original array
        const sortedLocations = response?.delivery_destinations?.toSorted(
          // @ts-expect-error FIXME: SDK DeliveryDestination vs hand-maintained one (TS2345)
          (a: DeliveryDestination, b: DeliveryDestination) => a.sort_sequence - b.sort_sequence,
        );

        // @ts-expect-error FIXME: SDK DeliveryDestination vs hand-maintained one (TS2322)
        this.siteDeliveryDestinations = sortedLocations;
      },
      async getSitesLocalMenuGroups(
        siteId: RecordEntityId,
      ): Promise<LocalMenuGroupToSiteRelationship[]> {
        const response = await this.serviceApi.get_menu_v3_local_menu_group_site(String(siteId));
        // @ts-expect-error FIXME: SDK DTO vs hand-maintained LocalMenuGroupToSiteRelationship (TS2322)
        this.siteLocalMenuGroups = response?.results;
        return this.siteLocalMenuGroups;
      },
      //  This method should be temporaly
      async getSitesForUser(): Promise<void> {
        this.loadingAllSites = true;
        // Stores for user config
        const authStore = useAuthStore();
        const { cdlUser } = storeToRefs(authStore);
        const multigroupStore = useMultigroupStore();
        const { multigroups } = storeToRefs(multigroupStore);

        // Fetch multigroups if user is not site operator
        if (isEmpty(multigroups.value)) {
          await multigroupStore.getMultiGroups();
        }

        const promises = multigroups.value?.map((multigroup) =>
          this.getUserMultigroupSites(multigroup.id, cdlUser.value?.id),
        );

        // Wait for all promises to resolve
        const results = await Promise.all(promises);

        // Flatten the array of arrays into a single array
        const flattenedResultsArray = results.flat().filter((result) => result !== null);

        this.sites = flattenedResultsArray;
        this.loadingAllSites = false;
      },
      // This method should be temporaly
      async getUserMultigroupSites(multigroupId: string, _userId: string): Promise<Site[]> {
        const response = await this.serviceApi.get_location_multigroup(multigroupId);

        // @ts-expect-error FIXME: SDK Group vs hand-maintained Site (TS2322)
        return response?.groups;
      },

      /**
       * @returns true if the site is Canadian, false otherwise
       */
      isCanadianSite(): boolean {
        return this.site?.address?.country?.toLowerCase() === CountryCode.CA.toLowerCase();
      },
      async createSite(payload: Partial<Site>): Promise<Site> {
        // @ts-expect-error FIXME: hand-maintained Site vs SDK Group body (TS2345)
        const response = await this.serviceApi.post_location_group(payload);
        // @ts-expect-error FIXME: SDK Group vs hand-maintained Site (TS2322)
        this.site = response;
        return this.site;
      },
      async createSiteViaEdge(payload: Partial<Site>): Promise<Site> {
        // @ts-expect-error FIXME: hand-maintained Site vs SDK SitePayloadDto (TS2345)
        const response = await this.serviceApi.post_centricos_site(payload);
        // @ts-expect-error FIXME: SDK CreateSiteResponse vs hand-maintained Site (TS2322)
        this.site = response;
        return this.site;
      },
      async addSiteToMultigroup(payload: Partial<Multigroup>): Promise<void> {
        // @ts-expect-error FIXME: hand-maintained Multigroup vs SDK MultiGroup body (TS2345)
        await this.serviceApi.patch_location_multigroup(String(payload.id), payload);
      },
      async getSitesForPriceLevel(priceLevelId: RecordEntityId): Promise<void> {
        const response = await this.serviceApi.get_sites_from_price_level(String(priceLevelId));
        // @ts-expect-error FIXME: SDK PriceLevelSite vs hand-maintained response type (TS2322)
        this.sitesUsingPriceLevel = response?.sites;
      },
      async updateBulkBrands(payload: PatchLocationBrandBulkBody) {
        if (payload) {
          await this.serviceApi.patch_location_brand_bulk(payload);
        } else {
          return Promise.reject(new Error('no payload selected'));
        }
      },

      setSiteStations(payload: { locationId: string; brands: StationBrand[] }) {
        if (payload) {
          const location = this.site.locations.filter((loc) => loc.id === payload.locationId)[0];
          location.brands = payload.brands;
        }
      },

      async deleteSite(siteId: RecordEntityId): Promise<void> {
        await this.serviceApi.delete_location_v2_group(String(siteId));
      },
      async restoreSite(siteId: RecordEntityId): Promise<void> {
        await this.serviceApi.post_location_group_restore(String(siteId));
      },
    },
    getters: {
      sitesStations: (state: State): StationBrand[] => {
        return (
          state.site?.locations?.flatMap((location) => {
            return location.brands;
          }) || []
        );
      },
      sitesLocationMap: (state: State): Record<RecordEntityId, SiteLocation> => {
        return state.site?.locations?.reduce(
          (acc: Record<RecordEntityId, SiteLocation>, val: SiteLocation) => {
            acc[val.id] = val;
            return acc;
          },
          {},
        );
      },
      isThriveSite: (state: State): boolean => {
        return state.site?.multigroup === import.meta.env.VITE_THRIVE_MULTIGROUP;
      },
      isBoostSite: (state: State): boolean => {
        return state.site?.multigroup === import.meta.env.VITE_BOOST_MULTIGROUP;
      },
      isThriveBoostSite(): boolean {
        return this.isThriveSite || this.isBoostSite;
      },
      isTacitSite: (state: State): boolean => {
        return state.site?.multigroup === import.meta.env.VITE_TACIT_MULTIGROUP;
      },
      isUnlistedSite: (state: State): boolean =>
        state.site?.locations?.[0]?.location_multigroup ===
        import.meta.env.VITE_UNLISTED_MULTIGROUP,
    },
    pagination: true,
    paginationConfig: {
      ...defaultSitePaginationConfig,
    },
  });
  return store();
}
